import { t } from "@lingui/macro";
import { List, SquaresFour } from "@phosphor-icons/react";
import { ScrollArea, Tabs, TabsContent, TabsList, TabsTrigger } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

import { queryClient } from "@/client/libs/query-client";
import { RESUMES_KEY } from "@/client/constants/query-keys";
import { GridView } from "./_layouts/grid";
import { ListView } from "./_layouts/list";

type Layout = "grid" | "list";

export const ResumesPage = () => {
  const [layout, setLayout] = useState<Layout>("grid");

  // Ensure resumes data is fresh when tab changes
  const handleLayoutChange = (value: string) => {
    setLayout(value as Layout);
    // Invalidate the resumes query to ensure fresh data when switching layouts
    queryClient.invalidateQueries({ queryKey: RESUMES_KEY });
  };

  // Ensure data is refreshed when component mounts
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: RESUMES_KEY });
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {t`Resumes`} - {t`Coach LMS`}
        </title>
      </Helmet>

      <Tabs
        value={layout}
        onValueChange={handleLayoutChange}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <motion.h1
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-bold tracking-tight"
          >
            {t`Resumes`}
          </motion.h1>

          <TabsList>
            <TabsTrigger value="grid" className="size-8 p-0 sm:h-8 sm:w-auto sm:px-4">
              <SquaresFour />
              <span className="ml-2 hidden sm:block">{t`Grid`}</span>
            </TabsTrigger>
            <TabsTrigger value="list" className="size-8 p-0 sm:h-8 sm:w-auto sm:px-4">
              <List />
              <span className="ml-2 hidden sm:block">{t`List`}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="h-[calc(100vh-140px)] lg:h-[calc(100vh-88px)]">
          <TabsContent value="grid">
            <GridView />
          </TabsContent>
          <TabsContent value="list">
            <ListView />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </>
  );
};
